import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { backend_api } from "../../../static/server";
import { appendTeamMemberLawyerId } from "../../../styles/styles";

export const COMMENT_NOTIFICATIONS_REFRESH_EVENT =
  "comment-notifications:refresh";

export const emitCommentNotificationsRefresh = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(COMMENT_NOTIFICATIONS_REFRESH_EVENT));
  }
};

const buildNotificationUrl = (basePath, teamMemberLawyerId) =>
  appendTeamMemberLawyerId(`${backend_api}${basePath}`, teamMemberLawyerId);

const useCommentNotifications = ({
  enabled = true,
  loadList = false,
  unreadOnly = false,
  pageSize = 12,
  pollingMs = 25000,
} = {}) => {
  const { token, teamMemberLawyerId } = useSelector((state) => state.user);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [error, setError] = useState("");

  const headers = useMemo(
    () => (token ? { Authorization: `Token ${token}` } : {}),
    [token],
  );

  const fetchUnreadCount = useCallback(async () => {
    if (!enabled || !token) return 0;
    setLoadingCount(true);
    try {
      const url = buildNotificationUrl(
        "comments/notifications/unread-count/",
        teamMemberLawyerId,
      );
      const { data } = await axios.get(url, { headers });
      const nextUnreadCount = Number(data?.unread_count || 0);
      setUnreadCount(nextUnreadCount);
      return nextUnreadCount;
    } catch (fetchError) {
      setError("Unable to load notification count.");
      return 0;
    } finally {
      setLoadingCount(false);
    }
  }, [enabled, headers, teamMemberLawyerId, token]);

  const fetchNotifications = useCallback(async () => {
    if (!enabled || !token || !loadList) return [];
    setLoadingList(true);
    try {
      const query = new URLSearchParams({
        page_size: String(pageSize),
      });
      if (unreadOnly) {
        query.set("unread", "true");
      }
      const url = buildNotificationUrl(
        `comments/notifications/?${query.toString()}`,
        teamMemberLawyerId,
      );
      const { data } = await axios.get(url, { headers });
      const results = data?.results || [];
      setNotifications(results);
      return results;
    } catch (fetchError) {
      setError("Unable to load notifications.");
      return [];
    } finally {
      setLoadingList(false);
    }
  }, [enabled, headers, loadList, pageSize, teamMemberLawyerId, token, unreadOnly]);

  const refresh = useCallback(async () => {
    if (!enabled || !token) return;
    await Promise.all([fetchUnreadCount(), loadList ? fetchNotifications() : Promise.resolve([])]);
  }, [enabled, fetchNotifications, fetchUnreadCount, loadList, token]);

  const markNotificationRead = useCallback(
    async (notificationId) => {
      if (!enabled || !token || !notificationId) return;
      const url = buildNotificationUrl(
        `comments/notifications/${notificationId}/read/`,
        teamMemberLawyerId,
      );
      await axios.patch(url, {}, { headers });
      emitCommentNotificationsRefresh();
    },
    [enabled, headers, teamMemberLawyerId, token],
  );

  const markThreadRead = useCallback(
    async (threadCommentId) => {
      if (!enabled || !token || !threadCommentId) return;
      const url = buildNotificationUrl(
        "comments/notifications/mark-thread-read/",
        teamMemberLawyerId,
      );
      await axios.post(
        url,
        { thread_comment_id: threadCommentId },
        { headers },
      );
      emitCommentNotificationsRefresh();
    },
    [enabled, headers, teamMemberLawyerId, token],
  );

  const markAllRead = useCallback(async () => {
    if (!enabled || !token) return;
    const url = buildNotificationUrl(
      "comments/notifications/mark-all-read/",
      teamMemberLawyerId,
    );
    await axios.post(url, {}, { headers });
    emitCommentNotificationsRefresh();
  }, [enabled, headers, teamMemberLawyerId, token]);

  useEffect(() => {
    if (!enabled || !token) return;
    refresh();
  }, [enabled, refresh, token]);

  useEffect(() => {
    if (!enabled || !token || pollingMs <= 0) return undefined;
    const intervalId = window.setInterval(() => {
      refresh();
    }, pollingMs);
    return () => window.clearInterval(intervalId);
  }, [enabled, pollingMs, refresh, token]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const handleRefresh = () => {
      refresh();
    };
    window.addEventListener(COMMENT_NOTIFICATIONS_REFRESH_EVENT, handleRefresh);
    return () =>
      window.removeEventListener(
        COMMENT_NOTIFICATIONS_REFRESH_EVENT,
        handleRefresh,
      );
  }, [enabled, refresh]);

  return {
    notifications,
    unreadCount,
    loadingList,
    loadingCount,
    error,
    refresh,
    fetchUnreadCount,
    fetchNotifications,
    markNotificationRead,
    markThreadRead,
    markAllRead,
  };
};

export default useCommentNotifications;
